import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import {
    CountyFilter,
    FilterClear,
    FilterGroup,
    HeaderFilter,
    Input,
    RoleFilter,
    StatusFilter,
    useDebounced,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    UserAccountActiveStatus,
    useUpdateFilterPreferences,
} from "modules/common";
import { mapToFilterName, UserAdministrationFilter } from "modules/common/utils/filters";
import React, { useEffect, useRef, useState } from "react";

const INPUT_DEBOUNCE_TIME = 500;

interface Props {
    roleFilter: number[];
    countyFilter: number[];
    statusFilter: UserAccountActiveStatus[];
    searchFilter: string;
    setCountyFilter: React.Dispatch<React.SetStateAction<number[]>>;
    setRoleFilter: (value: React.SetStateAction<number[]>) => void;
    setStatusFilter: (value: React.SetStateAction<UserAccountActiveStatus[]>) => void;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
}

export const AdministrationFilters: React.FC<Props> = ({
    roleFilter,
    countyFilter,
    statusFilter,
    searchFilter,
    setCountyFilter,
    setRoleFilter,
    setStatusFilter,
    setCurrentPage,
    setSearchFilter,
}) => {
    const [search, setSearch] = useState(searchFilter);
    const debouncedSearch = useDebounced(search, INPUT_DEBOUNCE_TIME);

    useEffect(() => {
        setSearchFilter(debouncedSearch);
        setCurrentPage(1);
    }, [debouncedSearch]);

    // dropdown states
    const [showCountyFilter, setShowCountyFilter] = useState(false);
    const [showRoleFilter, setShowRoleFilter] = useState(false);
    const [showStatusFilter, setShowStatusFilter] = useState(false);

    // refs
    const countyFilterRef = useRef<HTMLDivElement>(null);
    const roleFilterRef = useRef<HTMLDivElement>(null);
    const statusFilterRef = useRef<HTMLDivElement>(null);

    // outside click
    const handleCountyFilterOutsideClick = () => setShowCountyFilter(false);
    const handleRoleFilterOutsideClick = () => setShowRoleFilter(false);
    const handleStatusFilterOutsideClick = () => setShowStatusFilter(false);
    useOutsideClick(countyFilterRef, handleCountyFilterOutsideClick);
    useOutsideClick(roleFilterRef, handleRoleFilterOutsideClick);
    useOutsideClick(statusFilterRef, handleStatusFilterOutsideClick);

    // handle filter change
    const handleRoleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const valueNumb = parseInt(value);
        handleUpdateFilterPreferences(roleFilterName, valueNumb);
    };
    const handleCountyFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const valueNumb = parseInt(value);
        handleUpdateFilterPreferences(countyFilterName, valueNumb);
    };
    const handleStatusFilterChange = (status: UserAccountActiveStatus) =>
        handleUpdateFilterPreferences(statusFilterName, status);

    // filter clear
    const handleRoleFilterClear = () =>
        handleUpdateFilterPreferences(roleFilterName, undefined, true);
    const handleCountyFilterClear = () =>
        handleUpdateFilterPreferences(countyFilterName, undefined, true);
    const handleStatusFilterClear = () =>
        handleUpdateFilterPreferences(statusFilterName, undefined, true);
    const handleClearFilter = () => {
        setSearch("");
        handleRoleFilterClear();
        handleCountyFilterClear();
        handleStatusFilterClear();
    };

    // handle click
    const handleRoleFilterClick = () => setShowRoleFilter(prev => !prev);
    const handleCountyFilterClick = () => setShowCountyFilter(prev => !prev);
    const handleStatusFilterClick = () => setShowStatusFilter(prev => !prev);

    // handle filter change based on filter type
    const handleUpdateFilterPreferences = (
        filterName: string,
        filterValue?: number,
        clearFilter?: boolean
    ) => {
        updateFilterPreferencesMutation.mutate({
            filterName,
            filterIntValue: filterValue,
            clearFilter,
        });
        setCurrentPage(1);
    };

    const filterPreferencesQuery = useGetFilterPreferencesQuery([
        roleFilterName,
        countyFilterName,
        statusFilterName,
    ]);

    // query
    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    useEffect(() => {
        if (filterPreferencesQuery.some(s => s.isLoading)) return;

        const roleFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === roleFilterName
        )?.data?.filterIntValues;

        const countyFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === countyFilterName
        )?.data?.filterIntValues;

        const statusFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === statusFilterName
        )?.data?.filterIntValues;

        if (roleFilterData) setRoleFilter(roleFilterData);

        if (countyFilterData) setCountyFilter(countyFilterData);

        if (statusFilterData) setStatusFilter(statusFilterData);
    }, [filterPreferencesQuery]);

    return (
        <div className="f-r g-12 even">
            <HeaderFilter name="user-administration" title="KORISNICI">
                <FilterGroup>
                    <div ref={roleFilterRef}>
                        <RoleFilter
                            onChange={handleRoleFilterChange}
                            onClear={handleRoleFilterClear}
                            onClick={handleRoleFilterClick}
                            value={roleFilter}
                            isActive={roleFilter.length > 0}
                            showDropdown={showRoleFilter}
                        />
                    </div>

                    <div ref={countyFilterRef}>
                        <CountyFilter
                            onChange={handleCountyFilterChange}
                            onClear={handleCountyFilterClear}
                            onClick={handleCountyFilterClick}
                            value={countyFilter}
                            isActive={countyFilter.length > 0}
                            showDropdown={showCountyFilter}
                        />
                    </div>

                    <div ref={statusFilterRef}>
                        <StatusFilter
                            onChange={handleStatusFilterChange}
                            onClear={handleStatusFilterClear}
                            onClick={handleStatusFilterClick}
                            value={statusFilter}
                            isActive={Boolean(statusFilter.length)}
                            showDropdown={showStatusFilter}
                        />
                    </div>

                    <FilterClear
                        show={Boolean(
                            roleFilter.length || countyFilter.length || statusFilter.length
                        )}
                        onClearFilter={handleClearFilter}
                    />
                </FilterGroup>
            </HeaderFilter>

            <Input
                name="user-administration-search"
                id="user-administration-search"
                type="text"
                size="medium"
                placeholder="Pretraži korisnike."
                onChange={e => setSearch(e.target.value)}
                value={search}
                leftIcon={<SearchIcon className="search-icon" />}
            />
        </div>
    );
};

// map filter names
const roleFilterName = mapToFilterName<UserAdministrationFilter>(
    AdministrationFilters.name,
    "roleFilter"
);

const countyFilterName = mapToFilterName<UserAdministrationFilter>(
    AdministrationFilters.name,
    "countyFilter"
);

const statusFilterName = mapToFilterName<UserAdministrationFilter>(
    AdministrationFilters.name,
    "statusFilter"
);
